import { useEffect, useRef, useState } from 'react'
import { useScreenId } from '../../lib/useScreenId'
import { useScreenTrail } from '../../lib/sessionBack'
import type { ScreenKey } from '../../lib/screenIds'
import { useTranslation } from 'react-i18next'
import type { SessionMessage } from '../../types/session'
import { callClaude } from '../../lib/claude'
import { errorCodeOf, guideErrorKey } from '../../lib/guideErrors'
import { detectCrisis } from '../../lib/crisis'
import VoiceButton from '../ui/VoiceButton'
import CrisisAlert from '../ui/CrisisAlert'
import LoadingSpiral from '../ui/LoadingSpiral'

interface GroundProps {
  sessionId: string
  emotion: string
  intensity: number
  intentionText?: string
  firstName?: string
  seriesContext?: string
  /**
   * Awakening-only: short summary of recurring threads from past sessions.
   * Forwarded to callClaude → injected into phase 1 system prompt as
   * background awareness for the guide.
   */
  clusterContext?: string
  onComplete: (storyText: string) => void
}

type UIState = 'opening' | 'awaiting-story' | 'reflecting' | 'done' | 'error'

/** Which numbered screen each state shows (see lib/screenIds.ts). */
const SCREEN_OF: Record<UIState, ScreenKey> = {
  'opening':        'ground.story',
  'awaiting-story': 'ground.story',
  'reflecting':     'ground.story',
  'done':           'ground.mirror',
  'error':          'ground.error',
}

const FALLBACK_STARTERS = [
  "Something happened with someone close to me…",
  "I can't stop replaying something…",
  "There's something I haven't said out loud yet…",
  "I don't understand why I reacted the way I did…",
  "I've been carrying a feeling I can't fully name…",
  "I was blindsided when…",
]

function parseStarters(raw: string): string[] {
  return raw
    .split('\n')
    .map(line => line.replace(/^\d+\.\s*/, '').trim())
    .filter(line => line.length > 0)
    .slice(0, 6)
}

export default function Ground({ sessionId, emotion, intensity, intentionText = '', firstName = '', seriesContext, clusterContext, onComplete }: GroundProps) {
  const { t } = useTranslation()
  /*
   * Fallback starters come from the locale rather than the module-level
   * constant, so they are in the user's language while the personalised set
   * (fetched below) is still in flight. returnObjects is required for an array
   * value; the guard keeps a malformed locale from rendering an empty grid.
   */
  const fallbackStarters = (() => {
    const v = t('ground.starterLines', { returnObjects: true })
    return Array.isArray(v) && v.length ? (v as string[]) : FALLBACK_STARTERS
  })()

  const [uiState, setUiState] = useState<UIState>('opening')
  useScreenId(SCREEN_OF[uiState])
  // Back: the screens Ground has shown, in order (lib/sessionBack.ts). The two
  // loading states are never somewhere to return to.
  useScreenTrail<UIState>({
    screen: uiState,
    go: setUiState,
    kind: s => (s === 'opening' || s === 'reflecting' ? 'loading' : s === 'error' ? 'error' : 'rest'),
  })
  const [messages, setMessages] = useState<SessionMessage[]>([])
  const [claudeOpening, setClaudeOpening] = useState<string | null>(null)
  const [claudeMirror, setClaudeMirror] = useState<string | null>(null)
  // The story the current mirror was written for. Back can bring the person to
  // the story box after the guide has answered; pressing Continue on the same
  // words must show that answer again, not ask for a new one.
  const mirroredFor = useRef<string | null>(null)
  const [userStory, setUserStory] = useState('')
  const [starterUsed, setStarterUsed] = useState(false)
  // Why the last call failed, as a code the error screen translates (lib/claude.ts).
  const [errorCode, setErrorCode] = useState<string | null>(null)
  // Sticky once set — stays visible even if user edits the triggering text.
  const [showCrisis, setShowCrisis] = useState(false)
  const [starters, setStarters] = useState<string[]>(fallbackStarters)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const bottomRef = useRef<HTMLDivElement>(null)


  // Hide starters once user has typed something
  const showStarters = uiState === 'awaiting-story' && !userStory.trim()

  useEffect(() => {
    if (uiState === 'awaiting-story') textareaRef.current?.focus()
  }, [uiState])

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [claudeOpening, claudeMirror])

  async function fetchClaude(msgs: SessionMessage[]): Promise<string | null> {
    const result = await callClaude({
      phase: 1, emotion, intensity, messages: msgs,
      firstName, intentionText, sessionId, seriesContext, clusterContext,
    })
    if ('error' in result) {
      setErrorCode(errorCodeOf(result))
      setUiState('error')
      return null
    }
    return result.response
  }

  useEffect(() => {
    let cancelled = false

    // Fetch personalized starters (phase 0) and opening message (phase 1) in parallel
    const starterCall = callClaude({
      phase: 0, emotion, intensity, messages: [],
      firstName, intentionText, sessionId, seriesContext,
    }).then(result => {
      if (cancelled || 'error' in result) return
      const parsed = parseStarters(result.response)
      if (parsed.length >= 3) setStarters(parsed)
    })

    const openingCall = fetchClaude([]).then(res => {
      if (cancelled || !res) return
      setClaudeOpening(res)
      setMessages([{ role: 'assistant', content: res }])
      setUiState('awaiting-story')
    })

    void starterCall
    void openingCall

    return () => { cancelled = true }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function handleStarterClick(starter: string) {
    setUserStory(starter + ' ')
    setStarterUsed(true)
    setTimeout(() => {
      const el = textareaRef.current
      if (!el) return
      el.focus()
      el.setSelectionRange(el.value.length, el.value.length)
    }, 50)
  }

  async function handleSubmitStory() {
    if (!userStory.trim() || uiState === 'reflecting') return

    const story = userStory.trim()
    if (claudeMirror && mirroredFor.current === story) {
      setUiState('done')
      return
    }
    // The conversation is always the opening plus THIS story. After a change of
    // story the earlier story and its mirror are left out, so the guide answers
    // what is written now and not a blend of both.
    const updatedMessages: SessionMessage[] = [
      ...messages.slice(0, 1),
      { role: 'user', content: story },
    ]
    setUiState('reflecting')

    const mirror = await fetchClaude(updatedMessages)
    if (!mirror) return

    setMessages([...updatedMessages, { role: 'assistant', content: mirror }])
    setClaudeMirror(mirror)
    mirroredFor.current = story
    setUiState('done')
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && e.shiftKey) {
      e.preventDefault()
      handleSubmitStory()
    }
  }

  if (uiState === 'error') {
    return (
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-10 page-enter">
        <div className="max-w-sm w-full text-center">
          <p className="text-xs font-medium uppercase tracking-widest mb-4" style={{ color: '#9B72C0' }}>
            {t('errors.somethingWrong')}
          </p>
          <p className="text-sm leading-relaxed mb-2" style={{ color: '#1C1128' }}>
            {t('errors.guideConnect')}
          </p>
          {errorCode && (
            <p className="text-xs mt-2 mb-6 px-3 py-2 rounded-lg text-left"
              style={{ background: '#F4EFF8', color: '#6B5F7A' }}>
              {t(guideErrorKey(errorCode))}
            </p>
          )}
          <button
            onClick={() => {
              setUiState('opening')
              setErrorCode(null)
              fetchClaude([]).then(res => {
                if (!res) return
                setClaudeOpening(res)
                setMessages([{ role: 'assistant', content: res }])
                setUiState('awaiting-story')
              })
            }}
            className="px-6 py-3 rounded-xl text-sm font-medium"
            style={{ background: '#3D1F62', color: 'white', border: 'none', cursor: 'pointer' }}
          >
            {t('errors.tryAgain')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1 flex flex-col page-enter">
      <div className="flex-1 flex flex-col px-6 py-10 max-w-xl mx-auto w-full">

        {/* Claude opening */}
        {claudeOpening && (
          <div className="mb-8 message-appear">
            <p className="text-base leading-relaxed font-display italic" style={{ color: '#1C1128' }}>
              {claudeOpening}
            </p>
          </div>
        )}

        {/* Loading spiral */}
        {uiState === 'opening' && (
          <div className="mb-8">
            <LoadingSpiral size={22} />
          </div>
        )}

        {/* Story input */}
        {uiState === 'awaiting-story' && (
          <div className="flex-1 flex flex-col message-appear">

            {/* Sentence starters — visible only when textarea is empty */}
            {showStarters && (
              <div className="mb-5 message-appear">
                <p className="text-xs mb-3" style={{ color: '#9B8FAA' }}>
                  {t('ground.starters')}
                </p>
                <div className="grid grid-cols-1 gap-2">
                  {starters.map(s => (
                    <button
                      key={s}
                      onClick={() => handleStarterClick(s)}
                      className="text-left px-4 py-3 rounded-xl text-sm leading-relaxed transition-all"
                      style={{
                        // Subtle slate-blue tinted background — Ground phase accent at ~4% opacity
                        background: 'rgba(74,128,192,0.04)',
                        // Side-specific borders: 3px slate-blue accent on left, neutral elsewhere
                        borderTop: '1.5px solid #E4D5EE',
                        borderRight: '1.5px solid #E4D5EE',
                        borderBottom: '1.5px solid #E4D5EE',
                        borderLeft: '3px solid #4A80C0',
                        color: '#3D1F62',
                        cursor: 'pointer',
                      }}
                      onMouseEnter={e => {
                        // Hover: brighten the three neutral sides while preserving the accent
                        const el = e.currentTarget as HTMLButtonElement
                        el.style.borderTopColor = '#9B72C0'
                        el.style.borderRightColor = '#9B72C0'
                        el.style.borderBottomColor = '#9B72C0'
                      }}
                      onMouseLeave={e => {
                        const el = e.currentTarget as HTMLButtonElement
                        el.style.borderTopColor = '#E4D5EE'
                        el.style.borderRightColor = '#E4D5EE'
                        el.style.borderBottomColor = '#E4D5EE'
                      }}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Story textarea — wrapped in Ground blue card (#4A80C0) at 4% tint */}
            <div className="rounded-2xl px-5 py-5 mb-5" style={{
              background: 'rgba(74,128,192,0.04)',
              borderTop: '3px solid #4A80C0',
              borderRight: '1.5px solid #E4D5EE',
              borderBottom: '1.5px solid #E4D5EE',
              borderLeft: '1.5px solid #E4D5EE',
            }}>
              <p className="text-xs font-medium uppercase tracking-widest mb-3" style={{ color: '#4A80C0' }}>
                {t('ground.label')}
              </p>
              {/* Auto-grow textarea: starts at 160px (comfortably larger than the
                  old 5-row fixed height), expands to fit content as user types.
                  Fixes the mobile writing-too-cramped issue flagged in 9/10 of Reviewer 2's sessions. */}
              <VoiceButton
                onTranscript={t => setUserStory(prev => prev ? `${prev} ${t}` : t)}
                accentColor="#4A80C0"
              >
                <textarea
                  ref={textareaRef}
                  value={userStory}
                  onChange={e => {
                    const val = e.target.value
                    setUserStory(val)
                    if (!showCrisis && detectCrisis(val)) setShowCrisis(true)
                    // Auto-grow: reset to auto first so shrinking also works correctly
                    e.target.style.height = 'auto'
                    e.target.style.height = `${e.target.scrollHeight}px`
                  }}
                  onKeyDown={handleKeyDown}
                  placeholder={starterUsed ? t('ground.starterPlaceholder') : t('ground.placeholder')}
                  maxLength={5000}
                  className="w-full px-4 py-4 rounded-xl border text-sm leading-relaxed transition-all focus:outline-none"
                  style={{
                    borderColor: '#4A80C0',
                    background: 'white',
                    color: '#1C1128',
                    resize: 'none',
                    overflow: 'hidden',
                    minHeight: '160px',
                    paddingRight: '2.75rem',
                  }}
                  onFocus={e => { e.target.style.borderColor = '#4A80C0'; e.target.style.boxShadow = '0 0 0 3px rgba(74,128,192,0.18)' }}
                  onBlur={e => { e.target.style.borderColor = '#4A80C0'; e.target.style.boxShadow = 'none' }}
                />
              </VoiceButton>

              <p className="text-xs mt-2 hidden md:block" style={{ color: '#C4B5D4' }}>{t('ground.shiftEnter')}</p>
            </div>

            {showCrisis && <CrisisAlert />}

            {/* Focus prompt — shown when story is long (>400 words) so Claude can
                zero in rather than try to synthesise everything at once. */}
            {userStory.trim().split(/\s+/).length > 400 && (
              <div className="mt-3 px-4 py-3 rounded-xl" style={{ background: 'rgba(74,128,192,0.06)', border: '1px solid #D0E0F0' }}>
                <p className="text-sm" style={{ color: '#4A80C0', lineHeight: 1.6, margin: 0 }}>
                  {t('ground.focusPrompt')}
                </p>
              </div>
            )}

            <div className="flex-1" />

            <button
              onClick={handleSubmitStory}
              disabled={!userStory.trim()}
              className="mt-4 w-full py-4 rounded-xl font-medium tracking-wide transition-all"
              style={{
                background: userStory.trim() ? '#3D1F62' : '#E4D5EE',
                color: userStory.trim() ? 'white' : '#9B8FAA',
                border: 'none',
                cursor: userStory.trim() ? 'pointer' : 'not-allowed',
              }}
            >
              {t('session.continue')}
            </button>
          </div>
        )}

        {/* Reflecting */}
        {uiState === 'reflecting' && (
          <div className="message-appear">
            <LoadingSpiral size={22} label={t('session.generating')} />
          </div>
        )}

        {/* Mirror + continue */}
        {claudeMirror && uiState === 'done' && (
          <div className="flex-1 flex flex-col message-appear">
            <div
              className="mb-6 px-4 py-3 rounded-xl"
              style={{
                // Slate-blue tinted background — Ground phase accent at ~4% opacity
                background: 'rgba(74,128,192,0.04)',
                // Left-border accent recipe for small inline context cards
                borderTop: '1.5px solid #E4D5EE',
                borderRight: '1.5px solid #E4D5EE',
                borderBottom: '1.5px solid #E4D5EE',
                borderLeft: '3px solid #4A80C0',
              }}
            >
              <p className="text-xs mb-1" style={{ color: '#6B5F7A' }}>{t('ground.youWrote')}</p>
              <p className="text-sm leading-relaxed" style={{ color: '#3D1F62' }}>{userStory}</p>
            </div>

            <p className="text-base leading-relaxed font-display italic mb-8" style={{ color: '#1C1128' }}>
              {claudeMirror}
            </p>

            {showCrisis && <CrisisAlert />}

            <div className="flex-1" />

            <button
              onClick={() => onComplete(userStory)}
              className="w-full py-4 rounded-xl font-medium tracking-wide"
              style={{ background: '#3D1F62', color: 'white', border: 'none', cursor: 'pointer' }}
            >
              {t('session.continue')}
            </button>
          </div>
        )}

        <div ref={bottomRef} />
      </div>
    </div>
  )
}

